import React from 'react';

import { Box, Typography, useTheme } from '@material-ui/core';
import { HiOutlineLocationMarker } from 'react-icons/hi';
import { RiTeamLine } from 'react-icons/ri';

import { AppChip } from '@vyce/core/src/components';
import { CompanyWorkerItem, Team, TimeAndAttendanceLocation } from '@vyce/core/src/types';

import { LocationAudience } from './FormRecipientsModeStep';

const AUDIENCE_SUFFIX: Record<LocationAudience, string> = {
  everyone: '',
  clocked_in_now: ' · clocked in now',
  clocked_in_today: ' · clocked in today',
};

interface Props {
  selectedWorkers?: CompanyWorkerItem[];
  onRemoveWorker?: (worker: CompanyWorkerItem) => void;
  selectedTeams?: Team[];
  onRemoveTeam?: (team: Team) => void;
  selectedLocations?: TimeAndAttendanceLocation[];
  onRemoveLocation?: (location: TimeAndAttendanceLocation) => void;
  /** Who at the locations gets the form; shown on each location chip unless it's everyone. */
  locationAudience?: LocationAudience;
  emails?: string[];
  phones?: string[];
  onRemoveContact?: (contact: string) => void;
}

const SummarySection: React.FC<{ title: string; children?: React.ReactNode }> = ({ title, children }) => (
  <Box display="flex" flexDirection="column" gridGap={8}>
    <Typography variant="caption" color="textSecondary">
      {title}
    </Typography>
    <Box display="flex" flexWrap="wrap" gridGap={8}>
      {children}
    </Box>
  </Box>
);

export const FormRecipientsSummary: React.FC<Props> = ({
  selectedWorkers = [],
  onRemoveWorker,
  selectedTeams = [],
  onRemoveTeam,
  selectedLocations = [],
  onRemoveLocation,
  locationAudience = 'everyone',
  emails = [],
  phones = [],
  onRemoveContact,
}) => {
  const theme = useTheme();
  const contacts = [...emails, ...phones];
  const peopleCount = selectedWorkers.length + contacts.length;
  const total = peopleCount + selectedTeams.length + selectedLocations.length;

  if (total === 0) {
    return null;
  }

  // AppChip's label has zero padding, so the default MuiChip-icon negative margin
  // overlaps the text — override spacing on the icon element itself
  const chipIconStyle = { marginLeft: 0, marginRight: 6 };

  return (
    <Box display="flex" flexDirection="column" gridGap={16}>
      <Typography variant="body2" color="textSecondary">
        Sending to:
      </Typography>

      {peopleCount > 0 && (
        <SummarySection title="People">
          {selectedWorkers.map(worker => (
            <AppChip
              key={worker.user_id}
              label={`${worker.first_name} ${worker.last_name}`.trim()}
              onDelete={onRemoveWorker ? () => onRemoveWorker(worker) : undefined}
            />
          ))}
          {contacts.map(contact => (
            <AppChip
              key={contact}
              label={contact}
              onDelete={onRemoveContact ? () => onRemoveContact(contact) : undefined}
            />
          ))}
        </SummarySection>
      )}

      {selectedLocations.length > 0 && (
        <SummarySection title="Locations">
          {selectedLocations.map(location => (
            <AppChip
              key={location.uuid ?? location.name}
              icon={
                <HiOutlineLocationMarker
                  size="14px"
                  color={theme.palette.primary.main}
                  style={chipIconStyle}
                />
              }
              label={`${location.name}${AUDIENCE_SUFFIX[locationAudience]}`}
              onDelete={onRemoveLocation ? () => onRemoveLocation(location) : undefined}
            />
          ))}
        </SummarySection>
      )}

      {selectedTeams.length > 0 && (
        <SummarySection title="Teams">
          {selectedTeams.map(team => (
            <AppChip
              key={team.uuid ?? team.name}
              icon={<RiTeamLine size="14px" color={theme.palette.primary.main} style={chipIconStyle} />}
              label={team.name}
              onDelete={onRemoveTeam ? () => onRemoveTeam(team) : undefined}
            />
          ))}
        </SummarySection>
      )}
    </Box>
  );
};
